import React, { useMemo, useState } from 'react';

import AnimatedBottomSheet from './animatedBottomSheet';
import { Loader } from './loader';
import * as Icons from '../../assets/svg/icons';
import fallbackAvatar from '../../assets/images/userimg.png';
import { getActivePharmacistOptions } from '../../utils/helper/routeAuditUi';
import { normalizeBackendAssetUrl } from '../../utils/helper/url';

const PharmacistCardsSkeleton = () => (
    <div className="grid grid-cols-5 gap-2">
        {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex min-w-0 flex-col items-center gap-2">
                <div className="h-16 w-16 shrink-0 animate-pulse rounded-lg bg-gray-200" />
                <div className="h-3 w-14 animate-pulse rounded bg-gray-100" />
            </div>
        ))}
    </div>
);

const PharmacistNameMarquee = ({ text, isSelected }) => {
    const containerRef = React.useRef(null);
    const textRef = React.useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    React.useEffect(() => {
        const updateOverflowState = () => {
            if (!containerRef.current || !textRef.current) {
                return;
            }

            setIsOverflowing(textRef.current.scrollWidth > containerRef.current.clientWidth);
        };

        updateOverflowState();
        window.addEventListener('resize', updateOverflowState);
        return () => window.removeEventListener('resize', updateOverflowState);
    }, [text]);

    const textClassName = `text-center text-xs font-medium whitespace-nowrap ${isSelected ? 'text-blue-600' : 'text-slate-700'}`;

    if (!isOverflowing) {
        return (
            <div ref={containerRef} className="mx-auto flex w-full max-w-[5rem] items-center justify-center overflow-hidden">
                <p ref={textRef} className={textClassName} title={text}>
                    {text}
                </p>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="mx-auto w-full max-w-[5rem] overflow-hidden">
            <div className="dashboard-marquee-track" style={{ animationDuration: '8s' }}>
                <p ref={textRef} className={`${textClassName} shrink-0 pr-8`} title={text}>
                    {text}
                </p>
                <p className={`${textClassName} shrink-0 pr-8`} aria-hidden="true">
                    {text}
                </p>
            </div>
        </div>
    );
};

const formatPharmacistDisplayName = (value) =>
    `${value ?? ''}`
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');

export const AssistantPharmacistSwitchModal = ({
    isOpen,
    onClose,
    allPharmacistListResponse,
    currentPharmacistId,
    onSave,
    saving = false,
    errorMessage = '',
}) => {
    const pharmacistList = allPharmacistListResponse?.data?.data;
    const activePharmacists = useMemo(
        () => getActivePharmacistOptions(pharmacistList),
        [pharmacistList]
    );
    const [selectedPharmacistId, setSelectedPharmacistId] = useState(currentPharmacistId || '');

    React.useEffect(() => {
        if (isOpen) {
            setSelectedPharmacistId(currentPharmacistId || '');
        }
    }, [currentPharmacistId, isOpen]);

    const handleSave = () => {
        if (!selectedPharmacistId || saving) {
            return;
        }

        onSave(selectedPharmacistId);
    };

    return (
        <AnimatedBottomSheet isOpen={isOpen} onClose={onClose} fullScreen ariaLabel="Switch Pharmacist">
                    <div className="flex h-full min-h-0 flex-col bg-white">
                        <div className="flex items-center justify-between border-b border-dashed p-3 px-4">
                            <h3 className="text-sm font-bold text-gray-900">Switch Pharmacist</h3>
                            <button
                                type="button"
                                onClick={onClose}
                                className="ms-auto inline-flex h-8 w-8 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900"
                            >
                                <Icons.CloseModelIcon />
                                <span className="sr-only">Close modal</span>
                            </button>
                        </div>

                        <div className="otp-route-selection-scroller min-h-0 flex-1 overflow-y-auto bg-slate-50/70 px-2 py-2">
                            <div className="mx-auto flex w-full max-w-lg flex-col gap-3 px-2 py-3">
                            {allPharmacistListResponse?.loading ? (
                                <PharmacistCardsSkeleton />
                            ) : activePharmacists.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center">
                                    <p className="text-sm font-medium text-slate-600">No pharmacists available.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-5 gap-2">
                                    {activePharmacists.map((pharmacist) => {
                                        const pharmacistId = pharmacist?.id;
                                        const isSelected = String(selectedPharmacistId) === String(pharmacistId);
                                        const displayName = formatPharmacistDisplayName(pharmacist.name);
                                        const initials = displayName
                                            .split(' ')
                                            .map((n) => n[0])
                                            .join('')
                                            .toUpperCase()
                                            .slice(0, 2);
                                        const profileImageUrl = normalizeBackendAssetUrl(
                                            pharmacist?.profile_image
                                            || pharmacist?.profileImage
                                            || pharmacist?.photo
                                        );
                                        const isDefaultProfileImage = typeof profileImageUrl === 'string'
                                            && /\/user\.png(\?|#|$)/i.test(profileImageUrl);
                                        const colors = [
                                            'bg-blue-500',
                                            'bg-purple-500',
                                            'bg-pink-500',
                                            'bg-green-500',
                                            'bg-orange-500',
                                            'bg-red-500',
                                            'bg-indigo-500',
                                            'bg-cyan-500',
                                        ];
                                        const colorIndex = pharmacist.id % colors.length;
                                        const bgColor = colors[colorIndex];

                                        return (
                                            <button
                                                key={pharmacistId}
                                                type="button"
                                                onClick={() => setSelectedPharmacistId(pharmacistId)}
                                                className={`relative flex min-w-0 flex-col items-center gap-2 transition ${
                                                    isSelected ? 'opacity-100' : 'opacity-90 hover:opacity-100'
                                                }`}
                                            >
                                                <div className={`relative shrink-0 overflow-hidden rounded-lg transition ${
                                                    isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                                                }`}>
                                                    {profileImageUrl && !isDefaultProfileImage ? (
                                                        <img
                                                            src={profileImageUrl}
                                                            alt={displayName}
                                                            className="h-16 w-16 object-cover"
                                                            onError={(event) => {
                                                                event.currentTarget.src = fallbackAvatar;
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className={`flex h-16 w-16 items-center justify-center rounded-lg ${bgColor} text-lg font-bold text-white`}
                                                        >
                                                            {initials}
                                                        </div>
                                                    )}
                                                    {isSelected && (
                                                        <div className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 shadow-md">
                                                            <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                                <PharmacistNameMarquee
                                                    text={displayName}
                                                    isSelected={isSelected}
                                                />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {errorMessage ? <p className="mt-3 text-sm text-red-600">{errorMessage}</p> : null}
                            </div>
                        </div>

                        <div className="border-t border-slate-100 bg-white p-4">
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={!selectedPharmacistId || saving}
                                className="flex h-14 w-full items-center justify-center rounded-2xl bg-slate-900 text-lg font-semibold text-white shadow-[0_18px_45px_-22px_rgba(15,23,42,0.7)] transition hover:bg-slate-800 focus:ring-4 focus:ring-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {saving ? <Loader /> : 'Continue'}
                            </button>
                        </div>
                    </div>
        </AnimatedBottomSheet>
    );
};
